package com.arena_web.ui.components.overlay

import androidx.compose.runtime.*
import com.arena_web.data.dto.PersonDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.widgets.HiddenScrollbarStyle
import com.arena_web.ui.components.widgets.InlineSpinner
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.theme.bodyXlMedium
import com.arena_web.ui.theme.h2
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.*

// Figma 8285:184517 — popup locked to 932×793, 22px padding everywhere; both
// sections keep their slot heights so the popup never collapses under empty
// data.
private const val MODAL_BG = "#09142d"
private const val MODAL_W = 932
private const val MODAL_H = 793
private const val MODAL_PADDING = 22
private const val SECTION_GAP = 40
private const val HEADER_GAP_BELOW = 28
private const val SECTION_HEADER_H = 24
private const val SECTION_HEADER_GAP = 32
private const val NEW_LIKES_ROW_H = 252
private const val MATCHES_ROW_H = 240

// Count-badge swatches lifted straight from Figma — red for "needs your
// attention" likes, green for "mutual" matches.
private val BADGE_RED = Color.rgb(0xFF, 0x42, 0x42)
private val BADGE_GREEN = Color.rgb(0x2B, 0xAA, 0x1F)

// Section-underline gradient — shared with notification header / dropdowns.
private const val SECTION_DIVIDER_GRADIENT =
    "linear-gradient(90deg, " +
        "rgba(148, 149, 152, 0) 0%, " +
        "rgba(148, 149, 152, 0.27) 25%, " +
        "rgba(98, 99, 101, 0.3) 50%, " +
        "rgba(148, 149, 152, 0.27) 75%, " +
        "rgba(148, 149, 152, 0) 100%)"

// Halftone-dot flourishes baked into the design (Figma 8682:463233 + 461334),
// plus the diagonal-bar accent in the bottom-right corner (Figma 8682:461323).
// All three are PNGs lifted from Figma — re-creating them in CSS would lose
// the diagonal falloff / hand-tuned geometry, so we ship the artwork.
private const val BG_TOPRIGHT_URL = "/images/interactions-bg-topright.png"
private const val BG_BOTTOMLEFT_URL = "/images/interactions-bg-bottomleft.png"
private const val BG_BOTTOMRIGHT_URL = "/images/interactions-bg-bottomright.png"

/**
 * "Interactions" popup — opened from the header heart button. Mirrors Figma
 * 8285:184517: locked outer dimensions, sport-primary subtitle, red/green
 * count badges, twin halftone-dot background flourishes, two horizontal card
 * rows with per-section empty states sized to fill the slot so the popup
 * stays at design size even with no data.
 */
@Composable
fun InteractionsModal(
    isOpen: Boolean,
    isLoading: Boolean,
    newLikes: List<PersonDto>,
    matches: List<PersonDto>,
    sportName: String,
    onDismiss: () -> Unit,
    onNewLikeClick: (PersonDto) -> Unit,
    onPersonClick: (PersonDto) -> Unit,
) {
    if (!isOpen) return
    val sport = LocalSportColors.current
    val t = LocalAppStrings.current

    Box(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                property("position", "fixed")
                property("top", "0")
                property("left", "0")
                property("z-index", "1000")
                property("background", "rgba(0, 0, 0, 0.6)")
            }
            .onClick { onDismiss() },
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier
                .sWidth(MODAL_W)
                .sHeight(MODAL_H)
                .styleModifier {
                    property("position", "relative")
                    property("border-radius", s(24))
                    property("max-width", "calc(100vw - ${s(48)})")
                    property("max-height", "calc(100vh - ${s(48)})")
                    property("overflow", "hidden")
                    property("background", MODAL_BG)
                }
                .onClick { it.stopPropagation() }
        ) {
            // Background flourishes — the same halftone-dot artwork the Figma
            // file uses. Top-right pattern anchors to the top-right corner;
            // bottom-left mirrors at the opposite corner. Behind everything,
            // non-interactive.
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("position", "absolute")
                        property("top", "0")
                        property("right", "0")
                        property("width", s(177))
                        property("height", s(376))
                        property("background", "url('$BG_TOPRIGHT_URL') top right / 100% 100% no-repeat")
                        property("pointer-events", "none")
                    }
            )
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("position", "absolute")
                        property("bottom", "0")
                        property("left", "0")
                        property("width", s(195))
                        property("height", s(376))
                        property("background", "url('$BG_BOTTOMLEFT_URL') bottom left / 100% 100% no-repeat")
                        property("pointer-events", "none")
                    }
            )
            // Diagonal-bar accent anchored to the bottom-right corner (Figma
            // 8682:461323). Native trimmed PNG size is 374×323; the asset's
            // own transparency handles fade-in toward the popup centre.
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("position", "absolute")
                        property("bottom", "0")
                        property("right", "0")
                        property("width", s(374))
                        property("height", s(323))
                        property("background", "url('$BG_BOTTOMRIGHT_URL') bottom right / 100% 100% no-repeat")
                        property("pointer-events", "none")
                    }
            )

            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .styleModifier {
                        property("padding", s(MODAL_PADDING))
                        property("position", "relative")
                    }
            ) {
                Header(
                    newLikesCount = newLikes.size,
                    matchesCount = matches.size,
                    subtitleColor = sport.primary,
                    onDismiss = onDismiss,
                )

                Box(modifier = Modifier.sHeight(HEADER_GAP_BELOW))

                // Single centered spinner while the first response is in
                // flight. Once any data lands, sections take over — refreshes
                // happen silently in the background so we don't yo-yo back to
                // a loader on every reopen.
                val showInitialLoader = isLoading && newLikes.isEmpty() && matches.isEmpty()
                if (showInitialLoader) {
                    Box(
                        modifier = Modifier.fillMaxWidth().flexGrow(1),
                        contentAlignment = Alignment.Center,
                    ) {
                        InlineSpinner(size = 40.px)
                    }
                } else {
                    Section(
                        label = t.interactionsNewLikes,
                        count = newLikes.size,
                        badgeColor = BADGE_RED,
                        rowHeight = NEW_LIKES_ROW_H,
                        people = newLikes,
                        emptyIcon = AppIcons.EmptyLikes,
                        emptyTitle = t.interactionsNoNewLikesTitle,
                        emptyMessage = t.interactionsNoNewLikesMessage,
                        sportName = sportName,
                        onPersonClick = onNewLikeClick,
                    )

                    Box(modifier = Modifier.sHeight(SECTION_GAP))

                    Section(
                        label = t.interactionsMatches,
                        count = matches.size,
                        badgeColor = BADGE_GREEN,
                        rowHeight = MATCHES_ROW_H,
                        people = matches,
                        emptyIcon = AppIcons.EmptyMatches,
                        emptyTitle = t.interactionsNoMatchesTitle,
                        emptyMessage = t.interactionsNoMatchesMessage,
                        sportName = sportName,
                        onPersonClick = onPersonClick,
                    )
                }
            }
        }
    }
}

@Composable
private fun Header(
    newLikesCount: Int,
    matchesCount: Int,
    subtitleColor: Color,
    onDismiss: () -> Unit,
) {
    val t = LocalAppStrings.current
    Column(modifier = Modifier.fillMaxWidth().sGap(12)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.Bottom,
        ) {
            Column(modifier = Modifier.flexGrow(1).sGap(8)) {
                SpanText(
                    t.interactionsTitle,
                    modifier = Modifier
                        .color(AppColors.Text.Secondary)
                        .sFontSize(24)
                        .sLineHeight(32)
                        .sLetterSpacing(0.24)
                        .fontWeight(AppTypography.Medium)
                        .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                )
                SpanText(
                    t.interactionsSubtitle(newLikesCount, matchesCount),
                    modifier = Modifier
                        .color(subtitleColor)
                        .sFontSize(16)
                        .sLineHeight(24)
                        .sLetterSpacing(0.16)
                        .fontWeight(AppTypography.Regular)
                        .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                )
            }
            CloseButton(onClick = onDismiss)
        }
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(1.px)
                .styleModifier {
                    property("background", SECTION_DIVIDER_GRADIENT)
                    property("border-radius", "1px")
                }
        )
    }
}

@Composable
private fun CloseButton(onClick: () -> Unit) {
    // Same recipe as other dialog close buttons (FollowListDialog,
    // PersonDetailDialog): InteractiveSecondary fill on a #09142d base,
    // ButtonBorderGradientPrimary stroke applied through the mask-composite
    // overlay so the border keeps its blue gradient instead of the flat
    // rgba(255,255,255,0.4) hairline.
    Box(
        modifier = Modifier
            .sSize(44)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("position", "relative")
                property("border-radius", s(100))
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, #09142D, #09142D) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
                property("flex-shrink", "0")
            },
        contentAlignment = Alignment.Center,
    ) {
        Icon(src = AppIcons.Close, modifier = Modifier.sSize(16))
    }
}

@Composable
private fun Section(
    label: String,
    count: Int,
    badgeColor: Color,
    rowHeight: Int,
    people: List<PersonDto>,
    emptyIcon: String,
    emptyTitle: String,
    emptyMessage: String,
    sportName: String,
    onPersonClick: (PersonDto) -> Unit,
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier.sHeight(SECTION_HEADER_H),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                label,
                modifier = Modifier
                    .color(AppColors.Text.Secondary)
                    .sFontSize(18)
                    .sLineHeight(22)
                    .sLetterSpacing(0.09)
                    .fontWeight(AppTypography.Medium)
                    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
            )
            Box(modifier = Modifier.sWidth(16))
            Box(
                modifier = Modifier
                    .sSize(24)
                    .styleModifier {
                        property("border-radius", s(100))
                        property("background", "#${badgeColor.toHexString()}")
                    },
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    count.toString(),
                    modifier = Modifier
                        .color(AppColors.PrimaryWhite)
                        .sFontSize(14)
                        .sLineHeight(16)
                        .fontWeight(AppTypography.Medium)
                        .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                        // Urbanist's cap-height sits above the line-box center,
                        // so flex-centered digits read as too-high inside the
                        // pill. A 1px downward nudge restores optical centering.
                        .styleModifier { property("transform", "translateY(1px)") }
                )
            }
        }

        Box(modifier = Modifier.sHeight(SECTION_HEADER_GAP))

        Box(
            modifier = Modifier
                .fillMaxWidth()
                .sHeight(rowHeight),
            contentAlignment = Alignment.CenterStart,
        ) {
            if (people.isEmpty()) {
                EmptyState(
                    icon = emptyIcon,
                    title = emptyTitle,
                    message = emptyMessage,
                )
            } else {
                CardRow(people = people, sportName = sportName, onPersonClick = onPersonClick)
            }
        }
    }
}

@Composable
private fun CardRow(
    people: List<PersonDto>,
    sportName: String,
    onPersonClick: (PersonDto) -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                property("gap", s(18))
                property("overflow-x", "auto")
                property("align-items", "center")
            }
            .then(HiddenScrollbarStyle.toModifier()),
    ) {
        people.forEach { person ->
            InteractionCard(person = person, sportName = sportName, onClick = { onPersonClick(person) })
        }
    }
}

@Composable
private fun InteractionCard(
    person: PersonDto,
    sportName: String,
    onClick: () -> Unit,
) {
    Box(
        modifier = Modifier
            .styleModifier {
                property("flex-shrink", "0")
            }
            .cursor(Cursor.Pointer)
            .onClick { onClick() },
    ) {
        PlayerCard(
            playerName = person.fullName.takeIf { it.isNotBlank() } ?: person.username,
            rating = 0,
            position = "",
            sport = sportName,
            size = PlayerCardSize.Small,
            avatarUrl = person.avatarUrl,
            cardAssetsBasePath = person.cardAssetsBasePath,
            cardTextColor = person.cardTextColor,
            hideRating = true,
            hideShadow = false,
        )
    }
}

/**
 * Profile-style empty state — bare icon at natural size, then H2 title and a
 * BodyXL/M message stacked underneath. Matches `NoPostsEmptyState` /
 * `NoEventsEmptyState` so the popup's empty slots feel like the rest of the
 * app instead of bolting a one-off chrome-frame look on top.
 */
@Composable
private fun EmptyState(
    icon: String,
    title: String,
    message: String,
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                property("align-items", "center")
                property("justify-content", "center")
                property("gap", s(28))
                property("text-align", "center")
            },
    ) {
        // Multi-tone card illustration painted in the project-wide muted-blue
        // empty-state palette (`EMPTY_STATE_GLYPH_COLOR` family). Rendered at
        // its natural viewBox size so the card stack stays readable.
        Icon(src = icon)
        Column(
            modifier = Modifier.sGap(12),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(title, modifier = Modifier.h2().color(AppColors.Text.Secondary))
            Box(modifier = Modifier.sMaxWidth(420)) {
                SpanText(
                    message,
                    modifier = Modifier.bodyXlMedium().color(AppColors.Text.Secondary)
                )
            }
        }
    }
}

private fun Color.toHexString(): String {
    val rgb = toRgb()
    fun Int.hex2(): String = toString(16).padStart(2, '0')
    return "${rgb.red.hex2()}${rgb.green.hex2()}${rgb.blue.hex2()}"
}
